import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Store, TrendingDown, DollarSign, EyeOff, Users, Clock,
  Target, RefreshCw, Layers, TrendingUp, Briefcase, UserCog,
  Brain, Shield, Sparkles, CheckCircle, Lightbulb, Building2,
  ArrowRight, MapPin, ChevronDown, MessageSquare
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Use relative URLs so API calls work on any domain (preview + production)
const API_URL = "";
const SKILLVUE_LOGO = "https://cdn.prod.website-files.com/63eb9c5c0665608db409b4df/66cdde23b308922774c4b3e0_logomark.svg";
const SKILLVUE_TEXT = "https://cdn.prod.website-files.com/63eb9c5c0665608db409b4df/66d02c8992a92ee8b5595df6_Skillvue_logo_Type_Black.svg";

const ICON_MAP = {
  Store, TrendingDown, DollarSign, EyeOff, Users, Clock,
  Target, RefreshCw, Layers, TrendingUp, Briefcase, UserCog,
  Brain, Shield, Sparkles, CheckCircle, Lightbulb, Building2, MessageSquare
};

const SKILL_ICONS = [Users, Shield, Sparkles, CheckCircle, Lightbulb, Target];

const SKILL_COLORS = {
  blue: "bg-blue-50 text-blue-600",
  rose: "bg-rose-50 text-rose-600",
  amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600",
  purple: "bg-purple-50 text-purple-600",
  cyan: "bg-cyan-50 text-cyan-600",
};

export default function CasePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCase();
  }, [slug]);

  const fetchCase = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cases/${slug}`);
      if (!res.ok) {
        throw new Error("Case study non trovato");
      }
      const data = await res.json();
      setCaseData(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4B4DF7]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold text-slate-800">Case Study non trovato</h1>
        <p className="text-slate-500">{error}</p>
        <Button onClick={() => navigate("/")}>Torna alla home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Inter_Tight']">
      <Header companyName={caseData.company_name} />
      <HeroSection data={caseData} />
      <ClientSection data={caseData} />
      <ChallengeSection data={caseData} />
      <SolutionSection data={caseData} />
      <ResultsSection data={caseData} />
      <VisionSection data={caseData} />
      <CTASection />
    </div>
  );
}

function Header({ companyName }) {
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a href="https://www.skillvue.ai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
          <img src={SKILLVUE_LOGO} alt="Skillvue" className="h-7" />
          <span className="font-['Inter_Tight'] font-semibold text-lg text-[var(--sv-secondary)]">Skillvue</span>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
          <button onClick={() => document.getElementById('sfida')?.scrollIntoView({ behavior: 'smooth' })}>La Sfida</button>
          <button onClick={() => document.getElementById('soluzione')?.scrollIntoView({ behavior: 'smooth' })}>La Soluzione</button>
          <button onClick={() => document.getElementById('risultati')?.scrollIntoView({ behavior: 'smooth' })}>Risultati</button>
          <button onClick={() => document.getElementById('visione')?.scrollIntoView({ behavior: 'smooth' })}>Visione</button>
        </nav>
        <a href="https://www.skillvue.ai/contact-us" target="_blank" rel="noopener noreferrer">
          <Button className="bg-[var(--sv-primary)] hover:bg-[var(--sv-primary-hover)] text-white rounded-md h-9 px-4 text-sm font-semibold gap-1.5">
            Contattaci <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </a>
      </div>
    </header>
  );
}

function HeroSection({ data }) {
  return (
    <section className="pt-16 md:pt-24 pb-16 bg-gradient-to-b from-slate-50/80 to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-[#4B4DF7]/3 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex-1 max-w-2xl">
          <Badge className="mb-5 bg-[#4B4DF7]/8 text-[var(--sv-primary)] border-[#4B4DF7]/20 hover:bg-[#4B4DF7]/8 font-medium text-xs tracking-wide uppercase px-3 py-1">
            Case Study
          </Badge>
          <h1 className="font-['Inter_Tight'] text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-[var(--sv-secondary)] leading-[1.1]">
            {data.main_title?.split(" ").map((word, i) => (
              <span key={i} className={word.toLowerCase() === "pipeline" || word.toLowerCase() === "interna" ? "text-[var(--sv-primary)]" : ""}>
                {word}{" "}
              </span>
            ))}
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed max-w-xl">
            {data.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a href="https://www.skillvue.ai/contact-us" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[var(--sv-primary)] hover:bg-[var(--sv-primary-hover)] text-white rounded-md h-12 px-6 text-base font-semibold gap-2">
                Contattaci <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
            <button onClick={() => document.getElementById('sfida')?.scrollIntoView({ behavior: 'smooth' })} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[var(--sv-primary)] h-12 px-4">
              Scopri di più <ChevronDown className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-10 flex items-end gap-4">
            <div className="flex items-center gap-2">
              <img src={SKILLVUE_LOGO} alt="Skillvue" className="h-8" />
              <img src={SKILLVUE_TEXT} alt="Skillvue" className="h-5 opacity-50" />
            </div>
            <span className="text-slate-300 text-lg font-light pb-1">&times;</span>
            {data.company_logo_url && (
              <img src={data.company_logo_url} alt={data.company_name} className="h-12 rounded object-contain translate-y-3" />
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="w-full lg:w-auto lg:min-w-[340px]">
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#FFB74B]/10 to-transparent rounded-bl-full" />
            <div className="p-6 relative">
              <div className="flex items-center gap-3 mb-5">
                {data.company_logo_url && <img src={data.company_logo_url} alt={data.company_name} className="h-10 rounded" />}
                <div>
                  <h3 className="font-['Inter_Tight'] font-bold text-[var(--sv-secondary)]">{data.company_name}</h3>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Scheda Cliente</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div className="flex items-start gap-2.5">
                  <Building2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Settore</p>
                    <p className="text-sm font-semibold text-[var(--sv-secondary)]">{data.sector}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <TrendingUp className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Fatturato</p>
                    <p className="text-sm font-semibold text-[var(--sv-primary)]">{data.revenue}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Users className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Dipendenti</p>
                    <p className="text-sm font-semibold text-[var(--sv-secondary)]">{data.employees}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Punti Vendita</p>
                    <p className="text-sm font-semibold text-[var(--sv-secondary)]">{data.stores_count}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}

function ClientSection({ data }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="max-w-3xl">
          <Badge className="mb-4 bg-[#4B4DF7]/8 text-[var(--sv-primary)] border-[#4B4DF7]/20 hover:bg-[#4B4DF7]/8 font-medium text-xs tracking-wide uppercase px-3 py-1">
            Contesto
          </Badge>
          <h2 className="font-['Inter_Tight'] text-2xl md:text-3xl font-bold tracking-tight text-[var(--sv-secondary)] mb-4">
            {data.context_title}
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: data.context_text }} />
          {data.context_subtext && (
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">{data.context_subtext}</p>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function ChallengeSection({ data }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const businessIcons = [Store, TrendingDown, DollarSign];
  const hrIcons = [EyeOff, Users, Clock];

  return (
    <section id="sfida" className="py-20 md:py-28 bg-[var(--sv-surface)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
          <Badge className="mb-4 bg-red-50 text-red-700 border-red-200 hover:bg-red-50 font-medium text-xs tracking-wide uppercase px-3 py-1">
            La Sfida
          </Badge>
          <h2 className="font-['Inter_Tight'] text-2xl md:text-3xl font-bold tracking-tight text-[var(--sv-secondary)] mb-3">
            Il problema strutturale
          </h2>
          <p className="mt-3 text-base text-slate-500 max-w-2xl mb-12">
            {data.company_name} si confrontava con sfide che impattavano sia il business che l'HR.
          </p>
        </motion.div>

        {/* Business Challenges */}
        {data.business_challenges?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: 0.1 }} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-red-500" />
              <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">Business</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {data.business_challenges.map((ch, i) => {
                const Icon = businessIcons[i % businessIcons.length];
                return (
                  <Card key={i} className="p-5 h-full border-red-100 bg-white">
                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-red-500" />
                    </div>
                    <h3 className="font-['Inter_Tight'] font-semibold text-[var(--sv-secondary)] text-sm mb-2">{ch.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{ch.description}</p>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* HR Challenges */}
        {data.hr_challenges?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: 0.3 }} className="mb-16">
            <div className="flex items-center gap-2 mb-4">
              <UserCog className="w-5 h-5 text-amber-600" />
              <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">HR</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {data.hr_challenges.map((ch, i) => {
                const Icon = hrIcons[i % hrIcons.length];
                return (
                  <Card key={i} className="p-5 h-full border-amber-100 bg-white">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="font-['Inter_Tight'] font-semibold text-[var(--sv-secondary)] text-sm mb-2">{ch.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{ch.description}</p>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Objectives */}
        {data.objectives?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.4 }}>
            <Badge className="mb-4 bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50 font-medium text-xs tracking-wide uppercase px-3 py-1">
              Obiettivi di Collaborazione
            </Badge>
            <h2 className="font-['Inter_Tight'] text-2xl md:text-3xl font-bold tracking-tight text-[var(--sv-secondary)] mb-8">
              {data.objectives_title}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.objectives.map((obj, i) => {
                const icons = [Target, RefreshCw, Layers, TrendingUp];
                const Icon = icons[i % icons.length];
                return (
                  <Card key={i} className="p-5 border-slate-100 bg-white flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-lg bg-[#4B4DF7]/8 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[var(--sv-primary)]" />
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{obj.text}</p>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function SolutionSection({ data }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="soluzione" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
          <Badge className="mb-4 bg-[#4B4DF7]/8 text-[var(--sv-primary)] border-[#4B4DF7]/20 hover:bg-[#4B4DF7]/8 font-medium text-xs tracking-wide uppercase px-3 py-1">
            La Soluzione
          </Badge>
          <h2 className="font-['Inter_Tight'] text-2xl md:text-3xl font-bold tracking-tight text-[var(--sv-secondary)] mb-3">
            {data.solution_title}
          </h2>
          {data.solution_subtitle && (
            <p className="mt-3 text-base text-slate-500 max-w-2xl">{data.solution_subtitle}</p>
          )}
        </motion.div>

        {/* Soft Skills */}
        {data.soft_skills?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.15 }} className="mb-14 mt-10">
            <h3 className="font-['Inter_Tight'] text-lg font-semibold text-[var(--sv-secondary)] mb-6 flex items-center gap-2">
              <Brain className="w-5 h-5 text-[var(--sv-primary)]" />
              Le soft skill valutate
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {data.soft_skills.map((skill, i) => {
                const Icon = SKILL_ICONS[i % SKILL_ICONS.length];
                const colorClass = SKILL_COLORS[skill.color] || SKILL_COLORS.blue;
                return (
                  <div key={i} className="relative bg-gradient-to-br from-white to-slate-50 rounded-2xl p-6 border border-slate-100 shadow-[0_4px_20px_-4px_rgba(75,77,247,0.1)]">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[#4B4DF7]/5 to-transparent rounded-tr-2xl rounded-bl-full" />
                    <div className="relative flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-2xl ${colorClass} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon className="w-7 h-7" />
                      </div>
                      <p className="text-sm font-bold text-[var(--sv-secondary)] leading-snug">{skill.name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Solution Highlights */}
        {data.solution_highlights?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.3 }} className="mb-14">
            <div className="grid md:grid-cols-3 gap-4">
              {data.solution_highlights.map((h, i) => (
                <Card key={i} className="p-6 border-slate-100 bg-[var(--sv-surface)]">
                  <h4 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)] mb-3">{h.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">{h.description}</p>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function ResultsSection({ data }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="risultati" className="py-20 md:py-28 bg-[var(--sv-surface)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
          <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 font-medium text-xs tracking-wide uppercase px-3 py-1">
            Risultati
          </Badge>
          <h2 className="font-['Inter_Tight'] text-2xl md:text-3xl font-bold tracking-tight text-[var(--sv-secondary)] mb-8">
            Key Metrics & Impatto
          </h2>
        </motion.div>

        {/* Metrics */}
        {data.result_metrics?.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {data.result_metrics.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}>
                <Card className="p-6 border-slate-100 bg-white text-center">
                  <p className="text-4xl font-bold text-[var(--sv-primary)] font-['Inter_Tight'] mb-1">{m.value}</p>
                  <p className="text-sm font-semibold text-[var(--sv-secondary)]">{m.label}</p>
                  {m.sublabel && <p className="text-xs text-slate-400 mt-1">{m.sublabel}</p>}
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Qualitative Results */}
        {data.qualitative_results?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.3 }}>
            <h3 className="font-['Inter_Tight'] text-xl font-semibold text-[var(--sv-secondary)] mb-5">Impatto qualitativo</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.qualitative_results.map((r, i) => {
                const icons = [MessageSquare, TrendingUp, Building2, Users];
                const Icon = icons[i % icons.length];
                return (
                  <Card key={i} className="p-5 border-slate-100 bg-white">
                    <div className="w-9 h-9 rounded-lg bg-[#4B4DF7]/8 flex items-center justify-center mb-3">
                      <Icon className="w-4.5 h-4.5 text-[var(--sv-primary)]" />
                    </div>
                    <h4 className="font-['Inter_Tight'] font-semibold text-sm text-[var(--sv-secondary)] mb-1">{r.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{r.description}</p>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function VisionSection({ data }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="visione" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
          <Badge className="mb-4 bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-medium text-xs tracking-wide uppercase px-3 py-1">
            Visione
          </Badge>
          <h2 className="font-['Inter_Tight'] text-3xl md:text-4xl font-bold tracking-tight text-[var(--sv-secondary)] mb-3">
            {data.vision_title}
          </h2>
          {data.vision_subtitle && (
            <p className="text-base text-slate-500 max-w-2xl mb-8">{data.vision_subtitle}</p>
          )}
        </motion.div>

        {data.vision_points?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }}>
            <Card className="p-6 md:p-8 border-slate-100 bg-[var(--sv-surface)]">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">{data.vision_objective_title}</h3>
                  {data.vision_objective_subtitle && (
                    <p className="text-sm text-slate-500 mt-1">{data.vision_objective_subtitle}</p>
                  )}
                </div>
              </div>
              <div className="space-y-3 pl-4 border-l-2 border-[var(--sv-primary)]/20">
                {data.vision_points.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[var(--sv-primary)] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section className="py-20 md:py-28 bg-[var(--sv-secondary)] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 0.5 } : {}} transition={{ duration: 1 }} className="absolute top-20 left-10 w-72 h-72 bg-[#4B4DF7]/20 rounded-full blur-[100px]" />
        <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 0.3 } : {}} transition={{ duration: 1, delay: 0.2 }} className="absolute bottom-10 right-10 w-96 h-96 bg-[#FF6550]/15 rounded-full blur-[120px]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-medium text-slate-300">Pronto a trasformare la tua People Strategy?</span>
          </motion.div>
          <h2 className="font-['Inter_Tight'] text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            <motion.span initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }} className="block">
              Scopri come l'AI può identificare
            </motion.span>
            <motion.span initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.4 }} className="block bg-gradient-to-r from-[#8B8DF9] via-[#FF6550] to-[#FFB74B] bg-clip-text text-transparent">
              il talento nascosto nella tua rete
            </motion.span>
          </h2>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.8 }} className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <a href="https://www.skillvue.ai/contact-us" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[var(--sv-primary)] hover:bg-[var(--sv-primary-hover)] text-white rounded-full h-12 px-8 text-base font-semibold gap-2 shadow-lg shadow-[#4B4DF7]/25">
                Contattaci <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
            <a href="https://www.skillvue.ai" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full h-12 px-8 text-base font-medium">
                Visita Skillvue
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
